import { useMemo } from 'react';

import { MBDEventTracking } from '@web-platform/shared-banana-tracking/src/trackMBD/MBDEventTracking';
import {
  CONFIG_SERVICE_KEYS,
  FEATURE_DECISION_KEYS,
} from '@web-platform/shared-universal-constants-config';
import { getTrustedFunnel } from '@web-platform/shared-universal-redux/src/state';
import {
  getConfigServiceValue,
  getFeatureDecision,
  parseCsvConfigServiceValue,
} from '@web-platform/shared-universal-utils-frontend/src/config';
import { tryGetValue } from '@web-platform/shared-universal-utils-frontend/src/helpers/localStorage';
import { getCulture } from '@web-platform/shared-universal-utils-frontend-localisation';
import { useSelector } from 'react-redux';

const PARALLEL_SEARCH_STORAGE_NAMESPACE = 'parallelSearchValue'; // Owner: Capybara, Category: Essential, Reference: TTRI-63
const PACKAGES_PARALLEL_SEARCH_STORAGE_NAMESPACE = 'packagesParallelSearchValue'; // Owner: Capybara, Category: Essential, Reference: TTRI-158
const PARALLEL_SEARCH_STORAGE_KEY = 'value';

const getParallelSearchFromLocalStorage = (): boolean | undefined => {
  const parallelSearchValue = tryGetValue<boolean | undefined>(
    PARALLEL_SEARCH_STORAGE_KEY,
    undefined,
    PARALLEL_SEARCH_STORAGE_NAMESPACE,
  );

  return parallelSearchValue;
};

const getPackagesParallelSearchFromLocalStorage = (): boolean | undefined => {
  const packagesParallelSearchValue = tryGetValue<boolean | undefined>(
    PARALLEL_SEARCH_STORAGE_KEY,
    undefined,
    PACKAGES_PARALLEL_SEARCH_STORAGE_NAMESPACE,
  );

  return packagesParallelSearchValue;
};

export const useParallelSearch = (isEnabled: boolean) => {
  const { viewGuid } = useSelector(getTrustedFunnel);

  const isParallelSearchOptionEnabled =
    isEnabled && getFeatureDecision(FEATURE_DECISION_KEYS.ENABLE_PARALLEL_SEARCH_OPTION);

  const parallelSearchStorageEnabled = getFeatureDecision(
    FEATURE_DECISION_KEYS.HOMEPAGE_PARALLEL_SEARCH_CONSENT_ENABLED,
  );

  const defaultParallelSearchValue = useMemo(() => {
    if (!isParallelSearchOptionEnabled) {
      return getFeatureDecision(FEATURE_DECISION_KEYS.PARALLEL_SEARCH_DEFAULT_VALUE);
    }
    const storageValue = getParallelSearchFromLocalStorage();

    return parallelSearchStorageEnabled && storageValue !== undefined
      ? storageValue
      : getFeatureDecision(FEATURE_DECISION_KEYS.PARALLEL_SEARCH_DEFAULT_VALUE);
  }, [isParallelSearchOptionEnabled, parallelSearchStorageEnabled]);

  const parallelSearchTracking = useMemo(
    () =>
      viewGuid
        ? new MBDEventTracking({
            componentName: 'parallel_search',
            vertical: 'hotel',
            viewGuid,
          })
        : null,
    [viewGuid],
  );

  return [isParallelSearchOptionEnabled, defaultParallelSearchValue, parallelSearchTracking];
};

export const usePackagesParallelSearch = (
  isEnabled: boolean,
): readonly [boolean, boolean, MBDEventTracking | null] => {
  const { viewGuid } = useSelector(getTrustedFunnel);
  const { market } = getCulture();
  const packagesMarketsEnabled = parseCsvConfigServiceValue(
    getConfigServiceValue(CONFIG_SERVICE_KEYS.OPS.PACKAGES_MARKETS_ENABLED),
  );
  const isPackagesParallelSearchOptionEnabled =
    isEnabled &&
    getFeatureDecision(FEATURE_DECISION_KEYS.ENABLE_PACKAGES_PARALLEL_SEARCH_OPTION) &&
    packagesMarketsEnabled.includes(market);

  const packagesParallelSearchStorageEnabled = getFeatureDecision(
    FEATURE_DECISION_KEYS.HOMEPAGE_PARALLEL_SEARCH_CONSENT_ENABLED,
  );

  const packagesParallelSearchDefaultValue = useMemo(() => {
    if (!isPackagesParallelSearchOptionEnabled) {
      return false;
    }

    const storageValue = getPackagesParallelSearchFromLocalStorage();

    return packagesParallelSearchStorageEnabled && storageValue !== undefined
      ? storageValue
      : getFeatureDecision(FEATURE_DECISION_KEYS.PACKAGES_PARALLEL_SEARCH_DEFAULT_VALUE);
  }, [isPackagesParallelSearchOptionEnabled, packagesParallelSearchStorageEnabled]);

  const packagesParallelSearchTracking = useMemo(
    () =>
      viewGuid
        ? new MBDEventTracking({
            componentName: 'parallel_search',
            vertical: 'package',
            viewGuid,
          })
        : null,
    [viewGuid],
  );

  return [
    isPackagesParallelSearchOptionEnabled,
    packagesParallelSearchDefaultValue,
    packagesParallelSearchTracking,
  ];
};
